import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, DestroyRef, inject, input, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Subject, interval, switchMap, timer, tap, startWith } from "rxjs";

@Component({
    selector: "[app-catalog-window]",
    templateUrl: "./catalog-window.component.html",
    styleUrls: ["./catalog-window.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class CatalogWindowComponent implements OnInit {
  
  // Track the state of all windows
  private windowStates = new Map<string, boolean>();
  private lightingAnimation$ = new Subject<void>();

  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  public height = input<number>();
  public finalHeight = input<number>();
  
  public windowWidth: number = 30;
  public windowHeight: number = 31;

  public leftWindowPositions = computed(() => {
    if(this.height() === this.finalHeight()) {
      return this.generateWindows(6, this.height(), {
        xStart: 3,
        yStart: 130,
        rowMargin: 18,
        extraColumnMargin: 30,
        extraColumnMarginAfter9: 35,
        adjustRowMargin: true,
      })
    }
    return [];
  });

  public rightWindowPositions = computed(() => {
    if(this.height() === this.finalHeight()) {
      return this.generateWindows(6, this.height(), {
        xStart: 190,
        yStart: 220,
        rowMargin: 18,
        extraColumnMargin: 30,
        extraColumnMarginAfter9: 35,
        adjustRowMargin: false,
      })
    }
    return [];

  });

  ngOnInit(): void {
    // Setup interval for window lighting animation
    interval(5000)
      .pipe(
        startWith(1000),
        takeUntilDestroyed(this.destroyRef),
        tap(() => this.lightingAnimation$.next()),
        switchMap(() =>
          timer(3000).pipe(
            takeUntilDestroyed(this.destroyRef),
            tap(() => {
              this.windowStates.clear();
              this.cdr.detectChanges();
            })
          )
        )
      )
      .subscribe();

    // Handle window lighting animation
    this.lightingAnimation$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => {
          this.windowStates.clear();
          this.lightRandomWindows(84);
          this.cdr.detectChanges();
        })
      )
      .subscribe();
  }
  
  private getWindowId(columnIndex: number, rowIndex: number, isLeft: boolean): string {
    return `${isLeft ? "left" : "right"}-${columnIndex}-${rowIndex}`;
  }

  public isWindowLit(columnIndex: number, rowIndex: number, isLeft: boolean): boolean {
    const windowId = this.getWindowId(columnIndex, rowIndex, isLeft);
    return this.windowStates.get(windowId) || false;
  }

  private lightRandomWindows(count: number) {
    // Get total number of windows
    const leftTotal = this.leftWindowPositions().flat().length;
    const rightTotal = this.rightWindowPositions().flat().length;
    const totalWindows = leftTotal + rightTotal;

    // Generate random unique indices
    const selectedIndices = new Set<number>();
    while (selectedIndices.size < count) {
      selectedIndices.add(Math.floor(Math.random() * totalWindows));
    }

    // Light up selected windows
    selectedIndices.forEach((index) => {
      const isLeft = index < leftTotal;
      const localIndex = isLeft ? index : index - leftTotal;
      const columnIndex = Math.floor(localIndex / this.getRowCount(isLeft));
      const rowIndex = localIndex % this.getRowCount(isLeft);
      const windowId = this.getWindowId(columnIndex, rowIndex, isLeft);
      this.windowStates.set(windowId, true);
    });
  }

  private getRowCount(isLeft: boolean): number {
    return isLeft ? this.leftWindowPositions()[0].length : this.rightWindowPositions()[0].length;
  }

  private generateWindows(
    columns: number,
    initialHeight: number,
    config: {
      xStart: number;
      yStart: number;
      rowMargin: number;
      extraColumnMargin: number;
      extraColumnMarginAfter9: number;
      adjustRowMargin: boolean;
    }
  ) {
    const { xStart, yStart, rowMargin, extraColumnMargin, extraColumnMarginAfter9, adjustRowMargin } = config;
    const rowHeight = 18;
    const rows = Math.floor(initialHeight / (rowHeight + rowMargin));
    return Array.from({ length: columns }, (_, column) =>
      Array.from({ length: rows }, (_, row) => {
        const x = xStart + column * this.windowWidth;
        let y = yStart + row * rowHeight;
        if (adjustRowMargin && column > 0) {
          y += column * rowMargin;
        } else if (!adjustRowMargin && column > 0) {
          y -= column * rowMargin;
        }
        if (row > 2) {
          y += extraColumnMargin;
        }
        if (row > 8) {
          y += extraColumnMarginAfter9;
        }
        return { x, y };
      })
    );
  }

  public trackByIndex = (index: number) => index;
}
